import type { ActualPhoneType } from './phoneInputOptions';

export const DEFAULT_PHONE_COUNTRY = 'US';

export type PhoneCountry = {
  countryCode: string;
  displayName: string;
  callingCode: string | null;
};

export const FALLBACK_PHONE_COUNTRIES: PhoneCountry[] = [{ countryCode: DEFAULT_PHONE_COUNTRY, displayName: 'United States', callingCode: '+1' }];

type InternationalPhoneConfig = { features?: { internationalPhoneNumbers?: { isEnabled?: boolean } } } | null | undefined;

// Resumed loans carry their own flag (set by resume); new applications use the lender configuration.
export const isInternationalPhoneEnabled = (templateConfig: InternationalPhoneConfig, loanApplication?: { internationalPhoneNumbersEnabled?: boolean } | null): boolean =>
  loanApplication?.internationalPhoneNumbersEnabled ?? templateConfig?.features?.internationalPhoneNumbers?.isEnabled ?? false;

const US_PHONE_REGEX = /^\(\d{3}\)\s\d{3}-\d{4}$/;
const INTERNATIONAL_PHONE_REGEX = /^\d{1,15}$/;

export const isValidPhone = (value: string, country?: string): boolean => (!country || country === DEFAULT_PHONE_COUNTRY ? US_PHONE_REGEX : INTERNATIONAL_PHONE_REGEX).test(value);

// Omitted when disabled or empty: domestic payloads stay unchanged and an empty number lets the backend clear the country.
export const phoneCountryForWire = (enabled: boolean, number: string | undefined, country: string | undefined): string | undefined =>
  enabled && number ? country || DEFAULT_PHONE_COUNTRY : undefined;

type BorrowerPhoneCountryField = 'borrowerPrimaryPhoneCountry' | 'borrowerAlternatePhoneCountry' | 'borrowerWorkPhoneCountry';

export const applyPhoneCountryForStore = <T extends Record<string, unknown>>(
  borrower: T,
  field: BorrowerPhoneCountryField,
  enabled: boolean,
  number: string | undefined,
  country: string | undefined,
): T => {
  const value = phoneCountryForWire(enabled, number, country);
  const draft = borrower as Record<string, unknown>;
  if (value === undefined) {
    delete draft[field];
  } else {
    draft[field] = value;
  }
  return borrower;
};

export const persistComparable = <T>(value: T): T => JSON.parse(JSON.stringify(value ?? null));

export const countryFlagEmoji = (countryCode: string): string => countryCode.toUpperCase().replace(/[A-Z]/g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

export const formatPhoneWithCallingCode = (number: string, country: string | undefined, countries: PhoneCountry[]): string => {
  if (!number || !country) return number;
  const callingCode = countries.find((item) => item.countryCode === country)?.callingCode;
  return callingCode ? `${callingCode} ${number}` : number;
};

export const BORROWER_PHONE_COUNTRY_FIELDS = {
  mobilePhone: 'borrowerPrimaryPhoneCountry',
  homePhone: 'borrowerAlternatePhoneCountry',
  workPhone: 'borrowerWorkPhoneCountry',
} as const satisfies Record<ActualPhoneType, string>;

type BorrowerPhoneCountries = Partial<Record<(typeof BORROWER_PHONE_COUNTRY_FIELDS)[ActualPhoneType], string>> | null | undefined;

// Resume rehydration: overwrite any country left in CurrentForm by a previous session.
export const resumedPhoneCountryFormValues = (primary: BorrowerPhoneCountries, coBorrower?: BorrowerPhoneCountries) => ({
  phoneCountry: '',
  mobilePhoneCountry: primary?.borrowerPrimaryPhoneCountry || '',
  homePhoneCountry: primary?.borrowerAlternatePhoneCountry || '',
  workPhoneCountry: primary?.borrowerWorkPhoneCountry || '',
  mobilePhoneCoBorrowerCountry: coBorrower?.borrowerPrimaryPhoneCountry || '',
  homePhoneCoBorrowerCountry: coBorrower?.borrowerAlternatePhoneCountry || '',
  workPhoneCoBorrowerCountry: coBorrower?.borrowerWorkPhoneCountry || '',
});
